import { supabase } from "@/lovable-demo/runtime/client";

/**
 * Configurable thresholds, authority rules and CSR/workflow policy.
 *
 * Package #1 stores the values and reads them. The existing rule engines in
 * `recommend.ts` and `safety.ts` keep their current hard-coded behaviour so
 * nothing in the product moves; later packages switch them over to these keys.
 */

export interface PolicySetting {
  key: string;
  category: string;
  label: string;
  description: string;
  value: unknown;
  defaultValue: unknown;
  unit: string;
}

export async function listPolicySettings(): Promise<PolicySetting[]> {
  const { data } = await supabase.from("policy_settings").select("*").order("category");
  return (data ?? []).map((row) => ({
    key: row.key,
    category: row.category,
    label: row.label,
    description: row.description,
    value: row.value,
    defaultValue: row.default_value,
    unit: row.unit,
  }));
}

export async function getPolicyValue<T>(key: string, fallback: T): Promise<T> {
  const { data } = await supabase
    .from("policy_settings")
    .select("value")
    .eq("key", key)
    .maybeSingle();
  if (!data) return fallback;
  return (data.value as T) ?? fallback;
}

/** Requires the admin / quality-director role; RLS rejects everyone else. */
export async function setPolicyValue(
  key: string,
  value: unknown,
): Promise<{ error: string | null }> {
  const { data: userData } = await supabase.auth.getUser();
  const { error } = await supabase
    .from("policy_settings")
    .update({
      value: value as never,
      updated_by: userData.user?.id ?? null,
      updated_at: new Date().toISOString(),
    })
    .eq("key", key);
  return { error: error?.message ?? null };
}

export async function listAuthorityRules() {
  const { data } = await supabase
    .from("decision_authority_rules")
    .select("*")
    .eq("active", true)
    .order("key");
  return data ?? [];
}
